import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { auth, db } from "../firebase/firebaseConfig";
import { onAuthStateChanged, getIdToken, getIdTokenResult } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";

const ClaimsContext = createContext({
  user: null,
  role: null,     // 👈 role desde custom claims
  roleDb: null,   // 👈 rol leído desde Firestore (rol/role)
  claims: null,
  loading: true,
  refreshToken: async () => {},
});

export function ClaimsProvider({ children }) {
  const [user, setUser] = useState(null);
  const [claims, setClaims] = useState(null);   // claims completos
  const [roleDb, setRoleDb] = useState(null);   // rol Firestore
  const [loading, setLoading] = useState(true);

  // Auth listener + carga de claims
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u || null);
      setClaims(null);
      setRoleDb(null);

      if (!u) {
        setLoading(false);
        return;
      }

      try {
        // Refresca token para tener claims recientes
        await getIdToken(u, true);
        const res = await getIdTokenResult(u);
        setClaims(res?.claims || {});
      } catch (e) {
        console.error("Error obteniendo claims:", e);
        setClaims({});
      } finally {
        setLoading(false);
      }
    });

    return () => unsub();
  }, []);

  // Suscripción al doc de Firestore del usuario para obtener rol (rol/role)
  useEffect(() => {
    if (!user?.uid) {
      setRoleDb(null);
      return;
    }
    const ref = doc(db, "users", user.uid);
    const unsub = onSnapshot(ref, (snap) => {
      const data = snap.data() || {};
      const r = data.rol ?? data.role ?? null; // acepta ambos nombres
      setRoleDb(r);
    });
    return () => unsub();
  }, [user?.uid]);

  // Rol desde claims (si existe)
  const role = useMemo(() => (claims && claims.role) || null, [claims]);

  // Método público para refrescar claims bajo demanda
  const refreshToken = async () => {
    if (!auth.currentUser) return;
    await auth.currentUser.getIdToken(true);
    const res = await auth.currentUser.getIdTokenResult();
    setClaims(res?.claims || {});
  };

  const value = useMemo(
    () => ({ user, role, roleDb, claims, loading, refreshToken }),
    [user, role, roleDb, claims, loading]
  );

  return (
    <ClaimsContext.Provider value={value}>
      {loading ? (
        <div className="min-h-dvh grid place-items-center text-gray-600">
          <div className="flex items-center gap-3">
            <span className="animate-spin inline-block h-5 w-5 rounded-full border-2 border-gray-300 border-t-transparent" />
            <span>Iniciando sesión…</span>
          </div>
        </div>
      ) : (
        children
      )}
    </ClaimsContext.Provider>
  );
}

export function useClaims() {
  return useContext(ClaimsContext);
}